"use client";

import AuthModal from "@/components/Auth/AuthModal/page";
import { useLocale } from "@/hooks/use-locale";
import { LanguageDropdown } from "@/components/LanguageDropdown";
import { useTranslations } from "next-intl";
import React, { useState, useRef, useEffect } from "react";
import { Logo } from "../Logo";
import { getBrandConfig } from "@/lib/branding";

interface AuthPageProps {
  onLoginSuccess?: () => void;
}

const AuthPage: React.FC<AuthPageProps> = ({ onLoginSuccess }) => {
  const t = useTranslations();
  const brand = getBrandConfig();
  const { currentLocale, changeLanguage } = useLocale();
  const [mode, setMode] = useState<"register" | "login">("login");
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      const handleLoadedData = () => setIsVideoLoaded(true);
      video.addEventListener('loadeddata', handleLoadedData);

      // Проверка если видео уже загружено
      if (video.readyState >= 3) {
        setTimeout(() => setIsVideoLoaded(true), 0);
      }

      return () => video.removeEventListener('loadeddata', handleLoadedData);
    }
  }, []);

  const toggleMode = () => {
    setMode((prev) => (prev === "login" ? "register" : "login"));
  };

  return (
    <>
      {/* ── MOBILE LAYOUT (hidden on md+) ── */}
      <div
        className="md:hidden relative min-h-dvh w-full flex flex-col"
        style={{ backgroundColor: "#0a0035" }}
      >
        {/* Video background */}
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          <video
            ref={videoRef}
            className="auth-video"
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            onLoadedData={() => setIsVideoLoaded(true)}
            style={{ opacity: isVideoLoaded ? 1 : 0 }}
          >
            <source src="/auth-bg.mp4" type="video/mp4" />
          </video>
          {!isVideoLoaded && (
            <div
              className="auth-video-overlay"
              style={{ background: "linear-gradient(135deg, #0a0035 0%, #001a33 100%)" }}
            />
          )}
        </div>

        {/* Content above video */}
        <div className="relative z-10 flex flex-col items-center px-4 pt-14 pb-22 flex-1">
          {/* Logo at top */}
          <div className="mb-6 mt-4">
            <Logo variant="light" className="w-[160px] h-[auto]" />
          </div>

          {/* White form card */}
          <div className="w-full max-w-sm bg-white rounded-2xl p-6 shadow-xl">
            {/* Language switcher inside card, centered */}
            <div className="flex justify-center mb-4">
              <LanguageDropdown current={currentLocale} onChange={changeLanguage} />
            </div>

            {/* Auth form */}
            <AuthModal
              mode={mode}
              onToggle={toggleMode}
              onLoginSuccess={onLoginSuccess}
            />
          </div>

          {/* Branding below card */}
          <div className="mt-6 text-center px-4">
            <p
              className="uppercase text-center mb-3"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 300, fontSize: "8px", lineHeight: "100%", letterSpacing: "0%", color: "#FFFFFF" }}
            >
              {t("label-auth-subtitle-small")}
            </p>
            <p
              className="text-center mb-3"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 600, fontSize: "30px", lineHeight: "100%", letterSpacing: "0%", color: "#FFFFFF" }}
            >
              {brand.displayName}
            </p>
            {/* <div className="flex mb-3 justify-center">
              <Logo variant="light" className="w-[160px] h-[auto]" />
            </div> */}
            <p
              className="text-center"
              style={{ fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: "14px", lineHeight: "100%", letterSpacing: "0%", color: "#FFFFFF" }}
            >
              {t("label-auth-description")}
            </p>
          </div>
        </div>
      </div>

      {/* ── DESKTOP LAYOUT (hidden on mobile) ── */}
      <div className="hidden md:flex items-center justify-center min-h-screen p-4 w-full" style={{ backgroundColor: '#4E4E4E' }}>
        <div className="flex flex-row w-full max-w-5xl bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Left panel with video */}
          <div
            className="flex w-1/2 flex-col justify-between relative overflow-hidden"
            style={{
              padding: "40px",
              margin: "12px",
              borderRadius: "13px",
              backgroundColor: "#0a0035",
            }}
          >
            <div className="auth-video-bg" aria-hidden="true">
              <video
                className="auth-video"
                autoPlay
                loop
                muted
                playsInline
                preload="auto"
                onLoadedData={() => setIsVideoLoaded(true)}
                style={{ opacity: isVideoLoaded ? 1 : 0 }}
              >
                <source src="/auth-bg.mp4" type="video/mp4" />
              </video>
              {!isVideoLoaded && (
                <div
                  className="auth-video-overlay"
                  style={{ background: "linear-gradient(135deg, #0a0035 0%, #001a33 100%)" }}
                />
              )}
            </div>

            <div className="relative z-10" />

            {/* Description at bottom */}
            <div className="relative z-10">
              <p className="text-sm uppercase text-white/70 mb-2" />
              <div className="relative z-10">
                <Logo variant="light" className="w-[150px] h-[43px]" />
              </div>
              <p className="text-white/90 leading-relaxed">
                {t("label-auth-description")}
              </p>
            </div>
          </div>

          {/* Right panel: form */}
          <div className="w-1/2 p-8 flex flex-col">
            <div className="flex justify-center mb-4">
              <LanguageDropdown current={currentLocale} onChange={changeLanguage} />
            </div>

            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-md">
                <AuthModal
                  mode={mode}
                  onToggle={toggleMode}
                  onLoginSuccess={onLoginSuccess}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .auth-video-bg {
          position: absolute;
          inset: 0;
          overflow: hidden;
          border-radius: 13px;
          isolation: isolate;
          pointer-events: none;
        }

        .auth-video {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          z-index: 1;
          transition: opacity 0.5s ease-in;
        }

        .auth-video-overlay {
          position: absolute;
          inset: 0;
          z-index: 2;
          transition: background 0.5s ease-in;
        }

        @media (prefers-reduced-motion: reduce) {
          .auth-video {
            animation: none !important;
          }
        }
      `}</style>
    </>
  );
};

export default AuthPage;
